import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Shield, AlertCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

const PortalLoginPage = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email: formData.email,
        password: formData.password
      });

      if (authError) throw authError;

      if (authData.user) {
        // Check if user exists in portal_users table and their status
        const { data: portalUser, error: portalError } = await supabase
          .from('portal_users')
          .select('*')
          .eq('id', authData.user.id)
          .maybeSingle();

        if (portalError) {
          throw new Error('Failed to check portal account. Please try again.');
        }

        // Auto-create portal user if doesn't exist
        if (!portalUser) {
          const { error: insertError } = await supabase
            .from('portal_users')
            .insert({
              id: authData.user.id,
              email: authData.user.email,
              status: 'pending_approval',
              created_at: new Date().toISOString()
            });

          if (insertError) {
            throw new Error('Failed to create portal account. Please contact your administrator.');
          }

          toast.info('Your account is pending approval. An administrator will review your request.');
          navigate('/portal/pending-approval');
          return;
        }

        if (portalUser.status !== 'approved') {
          navigate('/portal/pending-approval');
          return;
        }

        // Update last login
        await supabase
          .from('portal_users')
          .update({ last_login: new Date().toISOString() })
          .eq('id', authData.user.id);

        toast.success('Login successful!');
        navigate('/portal/mydevices');
      }
    } catch (error: any) {
      console.error('Login error:', error);
      toast.error(error.message || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 to-secondary/5 flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-6">
        <img 
          src="/lovable-uploads/5b1ac4fa-502a-48e9-b8b6-7c1fe37496e8.png" 
          alt="smplify Logo" 
          className="h-10 sm:h-12 mx-auto mb-4"
        />
          <p className="text-muted-foreground">Device Management Portal</p>
        </div>

        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-center text-lg sm:text-xl">Welcome Back</CardTitle>
            <CardDescription className="text-center text-sm">
              Sign in to access your devices
            </CardDescription>
          </CardHeader>
          
          <form onSubmit={handleLogin}>
            <CardContent className="space-y-3 px-4 py-4">
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-sm">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="password" className="text-sm">Password</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <Alert className="py-2">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="text-xs">
                  Your account credentials are provided by your administrator. 
                  Contact support if you need assistance accessing your account.
                </AlertDescription>
              </Alert>
            </CardContent>

            <CardFooter className="flex flex-col space-y-3 px-4 pt-2">
              <Button
                type="submit"
                className="w-full"
                disabled={isLoading}
              >
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Sign In
              </Button>

              <div className="text-center">
                <p className="text-xs text-muted-foreground">
                  <Link to="/portal/forgot-password" className="text-primary hover:underline">
                    Forgot your password?
                  </Link>
                </p>
              </div>
            </CardFooter>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default PortalLoginPage;